import React, { FunctionComponent, useEffect, useState } from 'react';
import Layout from '../../components/Layout/Layout';
import { NavigationMenuContext } from '../../contexts/NavigationMenuContext';
import { ArticleModel } from '../../services/content/article/models/ArticleModel';
import { getArticleById } from '../../services/content/article/Article';
import { getNavbarCategories } from '../../services/configurator/navbar/NavbarConfigurator';
import { CategoryModel } from '../../services/content/category/models/CategoryModel';
import { getArticleBody } from '../../services/content/articleBody/ArticleBody';
import { ArticleBlockModel } from '../../services/content/articleBody/models/ArticleBlockModel';
import { ImageBlockModel } from '../../services/content/articleBody/models/ImageBlockModel';
import { EditorBlockModel } from '../../services/content/articleBody/models/EditorBlockModel';
import Head from 'next/head';
import { getRelatedById } from '../../services/content/related/Related';
import { ContentTypes, Pages } from '../../constants/Enums';
import { RelatedModel } from '../../services/content/related/models/RelatedModel';
import { getSpecialContent, getTemplateForArticle } from '../../services/configurator/spceialContent/SpecialContent';
import { ArticleTemplates } from '../../components/Article/ArticleTemplates/ArticleTemplates';
import { setupArticleTargeting } from '../../services/ads/Targeting';
import { TagModel } from '../../services/content/tag/models/TagModel';
import { OgMetaTags } from '../../components/Article/OgMetaTags/OgMetaTags';
import { LdJson } from '../../components/LdJson/LdJson';
import { cache } from '../../services/Cache';
import Script from 'next/script';
import { isURL } from '../../services/helpers/GeneralHelper';

type Props = {
    article: ArticleModel;
    body: (ArticleBlockModel | ImageBlockModel | EditorBlockModel | null)[];
    categories: CategoryModel[];
    related: RelatedModel[];
    template: string;
};

//TODO Add number of times read request
export async function getServerSideProps(context: any) {
    const { res } = context;
    res.setHeader('Cache-Control', 'public, s-maxage=14, stale-while-revalidate=59');
    const cacheKey = `/articles/${context.query.id}`;
    if (cache.get(cacheKey)) {
        const props = cache.get(cacheKey);
        if (props) {
            return {
                props: {
                    article: props.article,
                    related: props.related,
                    body: props.body,
                    categories: props.categories,
                    template: props.template,
                },
            };
        }
    }

    const article = await getArticleById(`${context.query.id}`);
    // Check if article id is null or undefined
    if (article.id === null || article.status !== 'active') {
        return { notFound: true };
    }

    const externalURL = article.urls?.externalUrl;
    if (externalURL && isURL(externalURL)) {
        res.writeHead(301, { location: externalURL });
        res.end();
        return { props: { article: null, related: null, body: null, categories: null, template: null } };
    }

    const related = await getRelatedById(`${context.query.id}`, ContentTypes.ARTICLE);
    const body = await getArticleBody(article.body);
    const categories = await getNavbarCategories();
    const specialContent = await getSpecialContent();
    const articleTemplate = getTemplateForArticle(article.id, article.category?.parentId, specialContent);

    cache.set(cacheKey, {
        article,
        related,
        body,
        categories,
        template: articleTemplate,
    });

    return {
        props: {
            article: article,
            related: related,
            body: body,
            categories: categories,
            template: articleTemplate,
        },
    };
}

const OpenArticle: FunctionComponent<Props> = ({ article, body, categories, related, template }) => {
    const [tags, setTags] = useState<TagModel[]>([]);

    useEffect(() => {
        const articleTags = related
            .filter((relatedItem) => relatedItem.type === 'tag')
            .map((relatedItem) => relatedItem.data as TagModel);

        setTags(articleTags);
        setupArticleTargeting(article, articleTags);
    }, []);

    let title = article.title;
    if (article.category?.title) {
        title += ' ' + article.category.title;
    }
    title += ' - woman.bg';

    return (
        <NavigationMenuContext.Provider value={categories}>
            <Layout>
                <Head>
                    <title>{title}</title>
                    <OgMetaTags article={article} />
                    <LdJson page={Pages.ARTICLE} article={article} />
                    <meta name='description' content={article.subtitle ?? ''} />
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `window['sprtlTargeting'] = {
                                section: ['news_view'],
                                category: ['${article?.category?.id ?? ''}'],
                                article: ['${article?.id ?? ''}'],
                                tag: [${tags.map((tag) => tag.id).join(',')}]
                            }`,
                        }}></script>
                    <script
                        dangerouslySetInnerHTML={{
                            __html: `
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('section', 'news_view') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('article', '${article.id}') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('category', '${article?.category?.id}') : '';
                         window.googletag && window.googletag.apiReady ? window.googletag.pubads().setTargeting('tag', ${JSON.stringify(tags.map((tag) => tag.id))}) : '';
                         `,
                        }}></script>
                    <script async src={'//cdn.jwplayer.com/libraries/KMyl3ECY.js'} />
                    <link rel='canonical' href={`${process.env.NEXT_PUBLIC_WEBSITE_URL}/article/${article.id}`} />
                </Head>
                <main className='px-md-2'>
                    <ArticleTemplates template={template} article={article} body={body} related={related} />
                    <Script
                        async
                        crossOrigin='anonymous'
                        src={`https://connect.facebook.net/bg_BG/sdk.js#xfbml=1&version=v18.0&appId=${process.env.NEXT_PUBLIC_SOCIAL_FB_APP_ID}&autoLogAppEvents=1`}
                        nonce='A6apfxi0'
                    />
                    <script async src='https://platform.twitter.com/widgets.js'></script>
                </main>
            </Layout>
        </NavigationMenuContext.Provider>
    );
};

export default OpenArticle;
